/**
 * UAPC Anytime - Vendor Dashboard
 * @module VendorDashboard
 * @author Sohail - Backend Developer
 * @author Osman - Frontend Developer
 * @description Vendor main dashboard with stats and overview
 * @version 1.0.0
 * Git: Sohail <sohail@linesoft.com> - Dashboard data
 * Git: Osman <osman@linesoft.com> - Dashboard UI
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, DollarSign, AlertTriangle, RefreshCw, TrendingUp, Clock, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import VendorLayout from '../../components/VendorLayout';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VendorDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dashboard/vendor`);
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <VendorLayout>
        <div className="p-8">
          <div className="animate-pulse space-y-6">
            <div className="h-10 bg-slate-200 dark:bg-slate-800 w-64" />
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-32 bg-slate-200 dark:bg-slate-800" />
              ))}
            </div>
          </div>
        </div>
      </VendorLayout>
    );
  }

  return (
    <VendorLayout>
      <div className="p-8" data-testid="vendor-dashboard">
        <h1 className="font-heading font-black text-3xl text-slate-900 dark:text-white uppercase tracking-tight mb-8">
          Vendor Dashboard
        </h1>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
              <span className="font-mono text-xs text-slate-500 uppercase">Total Parts</span>
            </div>
            <div className="font-heading font-black text-4xl text-slate-900 dark:text-white" data-testid="parts-count">
              {stats?.parts_count || 0}
            </div>
          </div>

          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <span className="font-mono text-xs text-slate-500 uppercase">Total Sales</span>
            </div>
            <div className="font-heading font-black text-4xl text-slate-900 dark:text-white" data-testid="total-sales">
              ${stats?.total_sales?.toFixed(2) || '0.00'}
            </div>
          </div>

          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-orange-600" />
              </div>
              <span className="font-mono text-xs text-slate-500 uppercase">Low Stock</span>
            </div>
            <div className="font-heading font-black text-4xl text-slate-900 dark:text-white" data-testid="low-stock">
              {stats?.low_stock_count || 0}
            </div>
          </div>

          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <RefreshCw className="w-6 h-6 text-purple-600" />
              </div>
              <span className="font-mono text-xs text-slate-500 uppercase">Last Sync</span>
            </div>
            <div className="font-heading font-bold text-lg text-slate-900 dark:text-white">
              {stats?.last_sync?.status === 'completed' ? (
                <span className="text-green-600">Synced</span>
              ) : stats?.last_sync ? (
                <span className="text-orange-600">{stats.last_sync.status}</span>
              ) : (
                <span className="text-slate-500">Never</span>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Recent Orders */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-bold text-xl text-slate-900 dark:text-white uppercase">
                Recent Orders
              </h2>
              <Link to="/vendor/orders" className="text-blue-600 hover:text-blue-700 text-sm font-bold flex items-center gap-1">
                View All <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            {stats?.recent_orders?.length > 0 ? (
              <div className="space-y-4">
                {stats.recent_orders.map((order, index) => (
                  <div key={order.id} className="flex items-center justify-between py-3 border-b border-slate-100 dark:border-slate-800 last:border-0">
                    <div>
                      <div className="font-mono text-xs text-slate-500">#{order.id.slice(0, 8)}</div>
                      <div className="font-medium text-slate-900 dark:text-white">
                        {order.items?.length || 0} items
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-heading font-bold text-blue-600">
                        ${order.total?.toFixed(2)}
                      </div>
                      <div className="text-xs text-slate-500">
                        {new Date(order.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">No recent orders</p>
            )}
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <Link 
              to="/vendor/inventory" 
              className="block bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 hover:border-blue-500 transition-colors group"
              data-testid="link-inventory"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-600 flex items-center justify-center">
                    <Package className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-lg text-slate-900 dark:text-white uppercase">
                      Manage Inventory
                    </h3>
                    <p className="text-sm text-slate-500">Add, edit, or remove parts</p>
                  </div>
                </div>
                <ChevronRight className="w-6 h-6 text-slate-400 group-hover:text-blue-600 transition-colors" />
              </div>
            </Link>

            <Link 
              to="/vendor/sync" 
              className="block bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 hover:border-blue-500 transition-colors group"
              data-testid="link-sync"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-600 flex items-center justify-center">
                    <RefreshCw className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-lg text-slate-900 dark:text-white uppercase">
                      MVS Sync
                    </h3>
                    <p className="text-sm text-slate-500">Synchronize with Motor Vehicle Software</p>
                  </div>
                </div>
                <ChevronRight className="w-6 h-6 text-slate-400 group-hover:text-blue-600 transition-colors" />
              </div>
            </Link>

            <Link 
              to="/vendor/reports" 
              className="block bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 hover:border-blue-500 transition-colors group"
              data-testid="link-reports"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-600 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-lg text-slate-900 dark:text-white uppercase">
                      View Reports
                    </h3>
                    <p className="text-sm text-slate-500">Sales and inventory analytics</p>
                  </div>
                </div>
                <ChevronRight className="w-6 h-6 text-slate-400 group-hover:text-blue-600 transition-colors" />
              </div>
            </Link>
          </div>
        </div>
      </div>
    </VendorLayout>
  );
};

export default VendorDashboard;
